import Term, { getUpdatedLevelString } from "../../term/Term";
import styles from "../css/LearningResultCell.module.css";
import { QuestionType } from "../../settings/model/QuestionType";
import TermLevelButton from "../../term/custom_view/TermLevelButton";
import { useState } from "react";
import { fetchGroupLanguageCode } from "../../groups/groupAPI";
import { getVoiceFile, makeBase64String } from "../../voice/util/voice";
import SpeakButton from "../../common/SpeakButton";
import AudioPlayer from "react-h5-audio-player";
import { isHiraganaOnly } from "../../common/strings";
import { useTranslation } from "react-i18next";
import AISentenceCard from "../../sentence/screen/AISentenceCard";
import SentenceGenerateButton from "../../sentence/view/SentenceGenerateButton";
import { SentenceGenerateButtonStatus } from "../../sentence/model/SentenceGenerateButtonStatus";
import MoreButton from "../../common/MoreButton";
import MarkButton from "../../common/MarkButton";
import {
  TermSentenceWithStringValue,
  convertExampleSentenceStringValue,
} from "../../sentence/model/Sentence";

interface LearningResultCellProps {
  term: Term;
  handleOnClickMarkButton: (term: Term) => void;
  handleOnClickLevelButton: (term: Term) => void;
  handleOnClickMoreButton: (
    event: React.MouseEvent<HTMLDivElement>,
    term: Term
  ) => void;
  handleOnClickGenerateSentenceButton: (term: Term) => void;
  handleOnClickSentenceLanguageButton: (term: Term) => void;
  handleOnClickMoreSentenceButton: (term: Term) => void;
  handleOnClickSentenceMoreButton: (
    event: React.MouseEvent<HTMLDivElement>,
    item: TermSentenceWithStringValue
  ) => void;
  config: {
    firstShow: QuestionType;
  };
}

export default function LearningResultCell(props: LearningResultCellProps) {
  const { t } = useTranslation();

  const [isPlaying, setIsPlaying] = useState(false);
  const [audioSrc, setAudioSrc] = useState<string>();

  const firstText =
    props.config.firstShow === QuestionType.termFirst
      ? props.term.term
      : props.term.definition;
  const secondText =
    props.config.firstShow === QuestionType.termFirst
      ? props.term.definition
      : props.term.term;
  const learningCount = props.term.learningCount ?? 0;

  function handleOnClickMarkButton(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    props.handleOnClickMarkButton(props.term);
  }

  function handleOnClickLevelButton(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    props.handleOnClickLevelButton(props.term);
  }

  function handleOnClickMoreButton(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    props.handleOnClickMoreButton(e, props.term);
  }

  function handleOnClickSentenceMoreButton(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    e.stopPropagation();
    if (props.term.sentence === undefined) return;
    const item = new TermSentenceWithStringValue(
      props.term,
      props.term.sentence,
      convertExampleSentenceStringValue(props.term.sentence)
    );
    props.handleOnClickSentenceMoreButton(e, item);
  }

  function handleOnClickGenerateSentenceButton(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    e.stopPropagation();
    props.handleOnClickGenerateSentenceButton(props.term);
  }

  function handleOnClickSentenceLanguageButton(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    e.stopPropagation();
    props.handleOnClickSentenceLanguageButton(props.term);
  }

  function handleOnClickMoreSentenceButton(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    e.stopPropagation();
    props.handleOnClickMoreSentenceButton(props.term);
  }

  function handleOnClickSpeakButton(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    speak();
  }

  async function speak() {
    let text = "";
    if (firstText === props.term.term) {
      text = isHiraganaOnly(props.term.pronunciation)
        ? props.term.pronunciation
        : props.term.term;
    } else {
      text = firstText;
    }

    const groupToSpeak =
      props.config.firstShow === QuestionType.termFirst || QuestionType.showAll
        ? props.term.group
        : "";

    setIsPlaying(true);
    try {
      const languageCode =
        groupToSpeak.length !== 0
          ? await fetchGroupLanguageCode(groupToSpeak)
          : "";
      const audio = await getVoiceFile(text, languageCode);
      setAudioSrc(makeBase64String(audio));
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  return (
    <>
      <div className={styles.container}>
        <div className={styles.cardContainer}>
          <div className={styles.termCardContainer}>
            <div className={styles.topContainer}>
              <div className={styles.topLeftContainer}></div>
              <div className={styles.topRightContainer}>
                <TermLevelButton
                  currentLevel={props.term.level}
                  handleOnClick={handleOnClickLevelButton}
                />
                <p className={styles.updatedLevelText}>
                  {getUpdatedLevelString(props.term.updatedLevel)}
                </p>
                <MoreButton onClick={handleOnClickMoreButton} />
              </div>
            </div>

            <div className={styles.termContainer}>
              <p className={styles.termText}>{firstText}</p>
              <p className={styles.definitionText}>{secondText}</p>
              {props.term.memo.length !== 0 && (
                <p className={styles.memoText}>{props.term.memo}</p>
              )}
              {props.term.pronunciation.length !== 0 && (
                <p className={styles.memoText}>{props.term.memo}</p>
              )}
            </div>

            <div className={styles.bottomContainer}>
              {Number(learningCount) !== 0 && (
                <p className={styles.learningCountText}>
                  {t("LearningResultCellLearningCount", { learningCount })}
                </p>
              )}
              <div className={styles.bottomRightContainer}>
                <MarkButton
                  isMarked={props.term.isMarked}
                  onClick={handleOnClickMarkButton}
                />

                <SentenceGenerateButton
                  status={
                    props.term.isGeneratingSentence
                      ? SentenceGenerateButtonStatus.onLoading
                      : props.term.sentence === undefined
                      ? SentenceGenerateButtonStatus.inactive
                      : SentenceGenerateButtonStatus.active
                  }
                  onClick={handleOnClickGenerateSentenceButton}
                />

                <SpeakButton
                  fontSize="small"
                  isPlaying={isPlaying}
                  onClick={handleOnClickSpeakButton}
                />
              </div>
            </div>
          </div>
          <AISentenceCard
            sentence={props.term.sentence}
            isMoreSentenceLoading={props.term.isMoreSentenceLoading}
            onClickLanguageButton={handleOnClickSentenceLanguageButton}
            onClickMoreSentenceButton={handleOnClickMoreSentenceButton}
            onClickMoreButton={handleOnClickSentenceMoreButton}
          />
        </div>
      </div>
      {audioSrc && (
        <AudioPlayer
          autoPlay={true}
          src={audioSrc}
          style={{ display: "none" }}
          onEnded={() => {
            setIsPlaying(false);
            setAudioSrc(undefined);
          }}
        />
      )}
    </>
  );
}
